import type { Control, UseFormWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Flag, CalendarDays, Columns3, User, Tag, Timer, FolderKanban } from 'lucide-react';
import { getColorValue } from '@/core/lib/color-contrast';
import { useDateFormat } from '@/shared/hooks/useDateFormat';
import type { ProjectStatus } from '@/features/tasks/public';
import type { Tag as ProjectTag } from '@/features/tags/public';
import { TaskFieldChip } from './TaskFieldChip';
import { TaskFieldEditor } from './TaskFieldEditor';
import type { FormFieldKey, TaskFieldOptions } from './TaskFieldEditor';
import { taskFieldChipValue } from '../utils/taskFieldChipValue';
import type { QuickTaskDraftFormValues } from '../schemas';
import type { QuickTaskProposal } from '../types';

/** Chip order and icon, as the design lays them out. Title and description are not chips. */
const CHIPS: { field: FormFieldKey; icon: React.ReactNode }[] = [
  { field: 'priority', icon: <Flag /> },
  { field: 'startDate', icon: <CalendarDays /> },
  { field: 'dueDate', icon: <CalendarDays /> },
  { field: 'assigneeId', icon: <User /> },
  { field: 'estimatedMinutes', icon: <Timer /> },
  { field: 'groupId', icon: <FolderKanban /> },
  { field: 'statusId', icon: <Columns3 /> },
  { field: 'tags', icon: <Tag /> },
];

interface ProposalChipRowProps {
  proposal: QuickTaskProposal;
  control: Control<QuickTaskDraftFormValues>;
  watch: UseFormWatch<QuickTaskDraftFormValues>;
  options: TaskFieldOptions;
  projectId: string;
  boardId: string;
  projectStatuses: ProjectStatus[];
  selectedTags: ProjectTag[];
  onTagsChange: (tags: ProjectTag[]) => void;
}

/** A palette token as a CSS colour, or null when there is none or the palette does not know it. */
function resolveColor(token: string | null | undefined): string | null {
  return token ? getColorValue(token) || null : null;
}

/**
 * Every interpreted field as a pill, in the order the design lays them out. Each opens its own
 * editor; the row's job is only to hand each chip what the board knows and what the proposal
 * knows, which are not the same thing for a value that does not exist yet.
 */
export function ProposalChipRow({
  proposal,
  control,
  watch,
  options,
  projectId,
  boardId,
  projectStatuses,
  selectedTags,
  onTagsChange,
}: ProposalChipRowProps) {
  const { t } = useTranslation('ai');
  const { dateFormat } = useDateFormat();

  /**
   * What the board does not have yet for this field, when the proposal named it: the editors all
   * pick from what exists, so they need telling about a value that does not.
   */
  function pendingValue(field: FormFieldKey) {
    const proposed = proposal.fields[field];
    if (!proposed?.isNew || !proposed.value) return null;
    // Only while the draft still holds the proposed name. Once the user picks something the
    // board already has, nothing is being created — and the payload stopped saying otherwise,
    // so the chip must stop saying it too.
    if (!draftStillHolds(field, proposed.value)) return null;
    return { label: proposed.value, color: resolveColor(proposed.color) };
  }

  /**
   * Whether the draft still carries the value the proposal named.
   *
   * Tags need their own answer: the draft holds a list while every other field holds one value,
   * so comparing the two directly was never equal — which turned the tags chip's "this will be
   * created" marker off for good, however new the tag was.
   */
  function draftStillHolds(field: FormFieldKey, proposedValue: string): boolean {
    if (field !== 'tags') return watch(field) === proposedValue;
    return (watch('tags') ?? []).includes(proposedValue);
  }

  /**
   * The chip's values, with the colours the board cannot supply filled in from the proposal:
   * a status or a tag that does not exist yet is in no option list, so it resolved colourless
   * and drew grey among the ones that do exist. The proposal knows the colour it will be
   * created with, and that is the colour it should read as before it is.
   */
  function chipParts(field: FormFieldKey) {
    const parts = taskFieldChipValue(field, watch(field), options, t, dateFormat);
    if (!parts) return null;

    if (field === 'tags') {
      return parts.map((part) => ({
        ...part,
        color:
          part.color ??
          resolveColor(proposal.tags.find((tag) => tag.name === part.label)?.color ?? null),
      }));
    }

    const proposed = proposal.fields[field];
    return parts.map((part) => ({ ...part, color: part.color ?? resolveColor(proposed?.color) }));
  }

  return (
    <div className="mt-3 flex flex-wrap gap-2.5">
      {CHIPS.map(({ field, icon }) => {
        const { provenance, candidates = [] } = proposal.fields[field];
        const pending = pendingValue(field);
        return (
          <TaskFieldChip
            key={field}
            field={field}
            provenance={provenance}
            icon={icon}
            label={t(`quickCreate.fieldLabels.${field}`)}
            value={chipParts(field)}
            isNew={pending !== null}
            isAmbiguous={candidates.length > 1}
          >
            <TaskFieldEditor
              field={field}
              pending={pending}
              candidates={candidates}
              control={control}
              options={options}
              projectId={projectId}
              boardId={boardId}
              projectStatuses={projectStatuses}
              selectedTags={selectedTags}
              onTagsChange={onTagsChange}
            />
          </TaskFieldChip>
        );
      })}
    </div>
  );
}
